// src/pages/knowledgeBase/SourceListPanel.jsx
//
// Generic list view for the three "browse-and-add" Layer-B catalogue
// source kinds: frameworks, catalogues, corporate registers.
// Type Baselines have their own dedicated panel (TypeBaselinesPanel)
// because they're generated from autopilot runs rather than uploaded.

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Stack,
  Group,
  Title,
  Text,
  Card,
  Badge,
  Button,
  Alert,
  Table,
  Anchor,
} from '@mantine/core';
import {
  IconRefresh,
  IconAlertCircle,
  IconUpload,
  IconExternalLink,
} from '@tabler/icons-react';
import { authFetch } from '../../utils/authFetch';
import { isRiskManager } from '../../utils/roleHelpers';

export default function SourceListPanel({
  kind,                 // 'framework' | 'catalogue' | 'corporate_register'
  title,
  description,
  emptyState,
  uploadDisabled = false,
}) {
  const { user } = useAuth0();
  const userIsRiskManager = useMemo(() => {
    const userRoles =
      user?.[`${window.location.origin}/roles`] ||
      user?.['https://agenthuginn.ai/roles'] ||
      user?.['https://riskgpt.app/roles'] ||
      [];
    return isRiskManager(userRoles);
  }, [user]);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`/.netlify/functions/listCatalogueSources?kind=${kind}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.rows || []);
    } catch (err) {
      console.error(`[SourceListPanel:${kind}] load error:`, err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    load();
  }, [load]);

  const isCorporate = kind === 'corporate_register';

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Title order={4}>{title}</Title>
          <Text size="sm" c="dimmed">{description}</Text>
        </Stack>
        <Group gap="xs">
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconRefresh size={14} />}
            onClick={load}
            loading={loading}
          >
            Refresh
          </Button>
          {userIsRiskManager && !uploadDisabled && (
            <Button
              size="xs"
              leftSection={<IconUpload size={14} />}
              disabled
              title="Upload UI lands in a follow-up; metadata is currently seeded via migrations."
            >
              Upload
            </Button>
          )}
        </Group>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>Failed to load: {error}</Alert>
      )}

      {!loading && rows.length === 0 && !error && (
        <Card withBorder p="xl">
          <Stack align="center" gap="xs">
            <Text c="dimmed">{emptyState || 'No sources yet.'}</Text>
          </Stack>
        </Card>
      )}

      {rows.length > 0 && (
        <Card withBorder p={0}>
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{isCorporate ? 'Register' : 'Source'}</Table.Th>
                <Table.Th>{isCorporate ? 'Org' : 'Publisher'}</Table.Th>
                <Table.Th>Version</Table.Th>
                <Table.Th>{isCorporate ? 'Last synced' : 'Source URL'}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.id}>
                  <Table.Td>
                    <Stack gap={2}>
                      <Text fw={600}>{r.name}</Text>
                      {r.description && (
                        <Text size="xs" c="dimmed" lineClamp={2}>{r.description}</Text>
                      )}
                      {r.slug && (
                        <Text size="xs" c="dimmed">
                          <code>{r.slug}</code>
                          {r.citation_format ? <> · cites as <code>{r.citation_format}</code></> : null}
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{isCorporate ? r.org_slug : (r.publisher || '—')}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="gray">{r.version || '—'}</Badge>
                  </Table.Td>
                  <Table.Td>
                    {isCorporate ? (
                      <Text size="sm" c="dimmed">
                        {r.last_synced_at ? new Date(r.last_synced_at).toLocaleString() : '—'}
                      </Text>
                    ) : r.source_url ? (
                      <Anchor href={r.source_url} target="_blank" rel="noopener noreferrer" size="sm">
                        <Group gap={4} wrap="nowrap">
                          Open
                          <IconExternalLink size={12} />
                        </Group>
                      </Anchor>
                    ) : (
                      <Text size="sm" c="dimmed">—</Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}
    </Stack>
  );
}
